import React, { useState, useRef, useEffect, useMemo } from 'react';
import { arrayHasItems } from '../../helpers/common';
import { CheckBadgeIcon, CheckIcon, ChevronDownIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { LoaderSpinner } from '../LoaderSpinner';

export const ForecastProductSelect = ({ 
    options, 
    selectedProduct,
    forecastData,
    handleChange, 
    access,
    handleOnCreate,
    optionsIsLoading,
    createIsLoading,
    placeholder = 'Select a product' 
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [search, setSearch] = useState('');
    const [selectedOption, setSelectedOption] = useState(selectedProduct || null);
    const dropdownRef = useRef(null);

    const filteredOptions = useMemo(() => {
        return options?.filter((option) =>
            option.label.toLowerCase().includes(search.toLowerCase())
        ) || [];
    }, [search, options]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleOptionClick = (option) => {
        setSelectedOption(option);
        handleChange?.(option);
        setIsOpen(false);
        setSearch('');
    };

    return (
        <div className="relative" ref={dropdownRef}>
            <button
                onClick={() => setIsOpen((prev) => !prev)}
                type="button"
                className="w-full px-4 py-2 text-left bg-white flex flex-row justify-between items-center border border-gray-300 rounded-md shadow-sm focus:ring focus:ring-blue-300 disabled:cursor-not-allowed disabled:bg-[#efefef4d]"
                disabled={optionsIsLoading || createIsLoading}
            >
                {options?.find((option) => option.value === selectedOption)?.label || placeholder}
                <div className='flex flex-row gap-2'>
                    {(forecastData?.month?.[selectedOption] || forecastData?.year?.[selectedOption]) && (
                        <CheckBadgeIcon className="h-5 w-5 text-green-700" />
                    )}
                    <ChevronDownIcon className="h-5 w-5" aria-hidden="true" />
                </div>
                
            </button>

            {isOpen && (
                <div className="absolute z-10 w-full mt-2 bg-white border border-gray-300 rounded-md shadow-lg">
                    <div className="relative">
                        <MagnifyingGlassIcon className="h-4 w-4 text-gray-400 absolute left-1.5 -translate-y-1/2 top-1/2" />
                        <input
                            type="text"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search products..."
                            className="w-full px-4 py-2 pb-1.5 pl-8 border-b border-gray-200 focus:outline-none"
                        />
                    </div>
                    <ul className="max-h-48 overflow-y-auto">
                        {createIsLoading ? (
                            <div className="py-2">
                                <LoaderSpinner height="50" width="50" border="5" colorSpinner="black" />
                            </div>
                        ) : (
                            arrayHasItems(filteredOptions) ? (
                                filteredOptions.map((option, index) => (
                                    <li
                                        key={index}
                                        onClick={() => handleOptionClick(option.value)}
                                        className={`flex justify-between px-4 py-2 cursor-pointer hover:bg-blue-200 ${selectedOption === option.value ? "bg-blue-200 font-semibold" : ""}`}
                                    >
                                        {option.label}
                                        {(forecastData?.month?.[option.value] || forecastData?.year?.[option.value]) && (
                                            <CheckBadgeIcon className="h-5 w-5 text-green-700" />
                                        )}
                                    </li>
                                ))
                            ) : (
                                <div>
                                    <li className="px-4 py-2 text-gray-500 w-full text-center">No results found</li>
                                    {access === "edit" && (
                                        <button
                                            className="px-4 py-2 hover:bg-blue-200 w-full"
                                            onClick={async (e) => {
                                                e.stopPropagation();
                                                if (search && handleOnCreate) {
                                                    await handleOnCreate?.({ name: search })
                                                }
                                            }}
                                        >
                                            + Create New Product: "{search}"
                                        </button>
                                    )}
                                </div>
                            )
                        )}
                    </ul>
                </div>
            )}
        </div>
    );
};